import React from 'react'
import moment, { Moment } from 'moment'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { withRouter, RouteComponentProps } from 'react-router-dom'

import { PromotionsTypes, IServiceObject, ICreatePromotion } from '../../../types'

import UIMessages from '../../../utils/ui-messages'
import { isNotBlank, isValidDate } from '../../../utils/validators'
import { Datepicker } from '@mobiscroll/react'

interface IPromotionFormProps {
  orgId: string
  onSubmit: (promotion: ICreatePromotion) => Promise<void>
  services: IServiceObject[]
}

interface IFormValues {
  title: string
  promotionType: PromotionsTypes
  promotionValue: number
  maxRedemptions: number
  expiresAt: string
  serviceId?: string
  note?: string
}

interface IFormProps {
  services: IServiceObject[]
}

const InnerForm = (props: IFormProps & FormikProps<IFormValues>) => {
  const { touched, errors, isSubmitting } = props

  return (
    <Form className='block ml2'>
      <div className='col-6'>
        <label className='font-small bold font-grey3'>{UIMessages.promotions.titleLabel}*</label>
        <Field name='title' type='text' />
        {touched.title && errors.title && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.title}</span>
        )}
      </div>

      <div className='col-6 mt1'>
        <label className='font-small bold font-grey3'>{UIMessages.promotions.promotionTypeLabel}*</label>
        <Field name='promotionType'>
          {({ field }) => (
            <select {...field} className='col-12'>
              <option value={PromotionsTypes.AMOUNT_OFF}>Amount deduction off</option>
              <option value={PromotionsTypes.PERCENT_OFF}>Percentage off</option>
            </select>
          )}
        </Field>
        {touched.promotionType && errors.promotionType && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.promotionType}</span>
        )}
      </div>

      {/* <div className='col-6 mt1'>
        <label className='font-small bold font-grey3'>{UIMessages.promotions.serviceLabel}</label>
        <Field name='serviceId'>
          {({ field }) => (
            <select {...field} className='col-12'>
              <option value={undefined} />
              {props.services.map(({ name, id }) => (
                <option value={id} key={`service_options_${id}`}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {touched.serviceId && errors.serviceId && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.serviceId}</span>
        )}
      </div> */}

      <div className='col-6 mt1'>
        <Field name='promotionValue'>
          {({ field, form }) => {
            return (
              <React.Fragment>
                <label className='font-small bold font-grey3'>
                  {UIMessages.promotions.promotionValueLabel} (
                  {form.values.promotionType === PromotionsTypes.AMOUNT_OFF ? 'Deduction of $' : '% off'}) *
                </label>
                <input className='col-12' type='number' {...field} />
              </React.Fragment>
            )
          }}
        </Field>
        {touched.promotionValue && errors.promotionValue && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.promotionValue}</span>
        )}
      </div>

      <div className='col-6 mt1'>
        <label className='font-small bold font-grey3'>{UIMessages.promotions.maxRedemptionsLabel}*</label>
        <Field name='maxRedemptions' type='number' className='col-12' />
        {touched.maxRedemptions && errors.maxRedemptions && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.maxRedemptions}</span>
        )}
      </div>
      <div className='col-6 mt1'>
        <label className='font-small bold font-grey3'>{UIMessages.common.note}</label>
        <Field name='note' className='col-12' />
      </div>

      <div className='col-6 mt1'>
        <label className='font-small bold font-grey3'>{UIMessages.promotions.expiresAtLabel}</label>
        <Field name='expiresAt'>
          {({ field, form }) => {
            const onChange = (date: Moment) => {
              form.setFieldValue(field.name, date.toDate())
            }
            return (
              <div className='col-12'>
                <Datepicker
                  controls={['calendar']}
                  dateFormat='MM/DD/YYYY'
                  max={moment().add(10, 'years')}
                  min={moment()}
                  value={form.values[field.name] ? moment(form.values[field.name]) : null}
                  onChange={({ value }) => onChange(moment(value))}
                />
              </div>
            )
          }}
        </Field>
        {touched.expiresAt && errors.expiresAt && (
          <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.expiresAt}</span>
        )}
      </div>
      <div className='mt1'>
        <button type='submit' className='SaveButton btn bg-blue-grey rounded font-white weight-300 mr4'>
          {UIMessages.common.save}
        </button>
      </div>
    </Form>
  )
}

const PromotionForm = withRouter(
  withFormik<IPromotionFormProps & RouteComponentProps, IFormValues>({
    mapPropsToValues: (props: IPromotionFormProps): IFormValues => {
      return {
        title: '',
        promotionType: PromotionsTypes.AMOUNT_OFF,
        promotionValue: 0,
        maxRedemptions: 1,
        expiresAt: '',
        note: ''
      }
    },
    validate: (values: IFormValues): FormikErrors<IFormValues> => {
      const errors: FormikErrors<IFormValues> = {}

      // The promotion code is required.
      if (!isNotBlank(values.title)) {
        errors.title = UIMessages.promotions.validation.promotionCodeError
      }

      // The value of the promotion should be more than zero.
      if (!values.promotionValue && values.promotionValue <= 0) {
        errors.promotionValue = UIMessages.promotions.validation.promotionValueError
      }

      // The maximum number of redemptions should be one or more.
      if (!values.maxRedemptions && values.maxRedemptions < 1) {
        errors.maxRedemptions = UIMessages.promotions.validation.promotionRedemptionError
      }

      // expiry is required and valid date
      if (!isValidDate(values.expiresAt)) {
        errors.expiresAt = UIMessages.promotions.validation.promotionExpiryAt
      }

      return errors
    },
    handleSubmit: async (value, { props, setSubmitting }) => {
      setSubmitting(true)

      const promotion: ICreatePromotion = {
        orgId: props.orgId,
        title: value.title,
        promotionType: value.promotionType,
        promotionValue: value.promotionValue,
        maxRedemptions: value.maxRedemptions,
        expiresAt: value.expiresAt,
        note: value.note
      }

      if (value.serviceId) {
        promotion.serviceId = value.serviceId
      }

      await props.onSubmit(promotion)
      props.history.push('/promotions')

      setSubmitting(false)
    }
  })(InnerForm)
)

export default PromotionForm



// WEBPACK FOOTER //
// ./src/components/promotions/forms/PromotionForm.tsx